import { Clipboard } from "@capacitor/clipboard";
import classNames from "classnames";
import { ChronoUnit, LocalDate } from "@js-joda/core";
import ons from "onsenui";
import React from "react";
import { PatientMedicationProfileWithDetails } from "../../../../messages/MedicationProfile";
import { STRINGS } from "../../../../strings";
import { globalize, translate } from "../../../../translate";
import { CALENDAR_SVG, LOCK_SVG } from "../../../../utils/svgs";
import {
  AppCard,
  AppCardContent,
  AppCardHeader,
  AppCardSubtitle,
  AppCardTitle,
} from "../../../Generic/AppCard";
import { AppIcon } from "../../../Generic/AppIcon";
import { AppList, AppListItem } from "../../../Generic/AppList";
import Flex from "../../../Generic/Flex";
import { If } from "../../../Generic/If";
import { IsDefined } from "../../../Generic/IsDefined";
import { openDatepicker } from "../../../MedflytDatepicker";
import MedicationCardFooter from "./MedicationCardFooter";

interface Props {
  medication: PatientMedicationProfileWithDetails;
  isDisabled: boolean;
  isEditable: boolean;
  isProfileSync: boolean;
  onChange: (newCard: PatientMedicationProfileWithDetails) => void;
  onRequestSave: () => void;
  onRequestToggleEdit: (editMode: boolean) => void;
  onRequestDelete: () => void;
  onClickProfileSync: () => void;
}

enum DateType {
  FROM,
  UNTIL,
}

const MedicationCard = (props: Props) => {
  const EXPIRATION_LIMIT = 20;
  const now: LocalDate = LocalDate.now();
  const expiry =
    props.medication.until !== null
      ? now.until(props.medication.until, ChronoUnit.DAYS)
      : undefined;

  const copyDate = (_dateType: DateType, date: LocalDate) => {
    Clipboard.write({ string: date.toString() })
      .then(() => ons.notification.toast("Copied text to clipboard", { timeout: 1000 }))
      .catch((err) =>
        ons.notification.toast(`Error copying text to clipboard ${err}`, { timeout: 1000 })
      );
  };

  const pasteDate = async (dateType: DateType) => {
    try {
      const { value } = await Clipboard.read();

      if (dateType === DateType.FROM) {
        props.onChange({
          ...props.medication,
          from: LocalDate.parse(value),
        });
      } else {
        props.onChange({
          ...props.medication,
          until: LocalDate.parse(value),
        });
      }
    } catch (err) {
      ons.notification.toast(`Error pasting ${err}`, { timeout: 1000 });
    }
  };

  const dateChanged = (dateType: DateType, date: LocalDate) => {
    switch (dateType) {
      case DateType.FROM:
        props.onChange({ ...props.medication, from: date });
        break;
      case DateType.UNTIL:
        props.onChange({ ...props.medication, until: date });
        break;
      default:
        console.error("Invalid date Type");
    }
  };

  const handleChangeNotes = (notes: any) => {
    props.onChange({ ...props.medication, notes: notes });
  };

  const handleChangeFrequency = (frequency: any) => {
    props.onChange({ ...props.medication, frequency: frequency });
  };

  const handleChangeDose = (dose: any) => {
    props.onChange({ ...props.medication, dose: dose });
  };

  const dateTrailing = (dateType: DateType, dateValue: LocalDate | null) => {
    return (
      <>
        {props.isEditable ? (
          <>
            <span
              className="dateText"
              onClick={() =>
                openDatepicker(
                  {
                    defaultDate: dateValue !== null ? dateValue.atStartOfDay() : undefined,
                    type: "date",
                    callbackObj: {
                      callback: (newDate) =>
                        newDate !== undefined ? dateChanged(dateType, newDate) : "",
                      cancelable: true,
                    },
                  },
                  translate(STRINGS.SELECT_DATE)
                )
              }
            >
              {dateValue !== null
                ? globalize().formatLocalDate(dateValue, { date: "long" })
                : translate(STRINGS.SELECT_DATE)}
            </span>
            <span className="pasteButton" onClick={() => pasteDate(dateType)}>
              ({translate(STRINGS.PASTE)})
            </span>
          </>
        ) : dateValue === null ? (
          <span
            className="MedicationCard__not_set_text"
            onClick={() => props.onRequestToggleEdit(true)}
          >
            {props.isDisabled
              ? translate(STRINGS.DATE_NOT_SPECIFIED)
              : translate(STRINGS.SELECT_DATE)}
          </span>
        ) : (
          <>
            <IsDefined value={dateValue}>
              {(date) => (
                <>
                  <span className="dateText">
                    {globalize().formatLocalDate(date, { date: "long" })}
                  </span>
                  <span className="copyButton" onClick={() => copyDate(dateType, date)}>
                    ({translate(STRINGS.COPY)})
                  </span>
                </>
              )}
            </IsDefined>
          </>
        )}
      </>
    );
  };

  return (
    <AppCard className={classNames("MedicationCard", { editable: props.isEditable })}>
      <AppCardHeader className="Row" borderless={true}>
        <Flex direction="column">
          <AppCardTitle>{props.medication.medicationName}</AppCardTitle>
          <AppCardSubtitle>
            {props.medication.dose}
            <If
              condition={
                !props.isEditable &&
                props.medication.frequency !== null &&
                props.medication.frequency !== ""
              }
            >
              {", " + props.medication.frequency}
            </If>
          </AppCardSubtitle>
          <If condition={props.isEditable}>
            <AppListItem className="MedicationCard__doseItem" title={translate(STRINGS.DOSE)} />
            <input
              className={"MedicationCard__doseInput"}
              type="text"
              placeholder="Enter dose..."
              value={props.medication.dose}
              onChange={(event) => handleChangeDose(event.target.value)}
            />
            <AppListItem
              className="MedicationCard__frequencyItem"
              title={translate(STRINGS.FREQUENCY)}
            />
            <input
              className={"MedicationCard__frequencyInput"}
              type="text"
              placeholder="Enter frequency..."
              value={props.medication.frequency !== null ? props.medication.frequency : ""}
              onChange={(event) => handleChangeFrequency(event.target.value)}
            />
          </If>
        </Flex>
        <If condition={props.isDisabled}>
          <AppIcon icon={LOCK_SVG} fill="gray" size={16} />
        </If>
      </AppCardHeader>
      <AppCardContent>
        <AppList>
          <AppListItem
            className="MedicationCard__date dateFrom"
            leading={props.isEditable ? CALENDAR_SVG : undefined}
            title={translate(STRINGS.FROM)}
            trailing={dateTrailing(DateType.FROM, props.medication.from)}
          />
          <AppListItem
            className="MedicationCard__date dateTo"
            leading={props.isEditable ? CALENDAR_SVG : undefined}
            title={translate(STRINGS.UNTIL)}
            trailing={dateTrailing(DateType.UNTIL, props.medication.until)}
          />
          <IsDefined value={expiry}>
            {(expiration) => (
              <AppListItem
                title=""
                trailing={
                  expiration <= EXPIRATION_LIMIT ? (
                    <label className="expiryLabel">
                      {translate(STRINGS.EXPIRES_IN) + " " + expiry + " " + translate(STRINGS.DAYS)}
                    </label>
                  ) : (
                    ""
                  )
                }
              />
            )}
          </IsDefined>
          <AppListItem className="MedicationCard__notesItem" title={translate(STRINGS.NOTES)} />
          {props.isEditable ? (
            <textarea
              rows={3}
              className={"MedicationCard__notesInput"}
              value={props.medication.notes !== null ? props.medication.notes : ""}
              placeholder={translate(STRINGS.ADD_NOTES)}
              onChange={(event) => handleChangeNotes(event.target.value)}
            />
          ) : (
            <div
              className={`MedicationCard__notes ${
                props.medication.notes === null ? `MedicationCard__notes_empty` : ""
              }`}
              onClick={() => props.onRequestToggleEdit(true)}
            >
              {props.medication.notes !== null
                ? props.medication.notes
                : translate(STRINGS.ADD_NOTES)}
            </div>
          )}
        </AppList>
      </AppCardContent>
      <If condition={!props.isDisabled}>
        <MedicationCardFooter
          isEditable={props.isEditable}
          isProfileSync={props.isProfileSync}
          onRequestSave={props.onRequestSave}
          onRequestToggleEdit={props.onRequestToggleEdit}
          onRequestDelete={props.onRequestDelete}
          onClickProfileSync={props.onClickProfileSync}
        />
      </If>
    </AppCard>
  );
};

export default React.memo(MedicationCard);
